import type { ReactNode } from 'react'
import type { PromoCode } from '../promo/codes'
import { openPromoModal } from '../promo/modal'
import { usePromo } from '../promo/store'
import { ArrowIcon, CheckIcon } from './ui/PromoIcons'

/**
 * The bar beneath the site's header, on every page. Navy with a drifting
 * purple glow while the visitor's promo is on offer, where a click opens the
 * sign-up form that earns it, and the glow holds still for a visitor who asks
 * for reduced motion; green and blue with a check once it is active, saying the checkout
 * applies it. Its words follow the visitor's code, so each test group reads
 * its own offer.
 *
 * The sign-up form is the site's, not ours: a modal built in Webflow, which the
 * bar opens, and the page's code activates the promo once the form has
 * signed the visitor up, through `Codeverse.promo.activate(code)`. The promo
 * store holds the state and hydrates at bundle load, so a visitor who signed
 * up, or followed a promo link, sees it applied on the first paint of every page.
 */

export interface PromoBannerProps {
  /**
   * The Webflow modal the bar opens (`data-modal`), as a CSS selector: the
   * site's `.contact-modal5_component` without it, one for every visitor, or
   * one per code as JSON, such as
   * `{"WELCOME40":".contact-modal5_component","FIRSTFREE":".contact-modal6_component"}`.
   */
  modal?: unknown
}

interface Wording {
  /** The navy bar's call to sign up. */
  offer: ReactNode
  /** The green bar, from `sm` up. */
  applied: ReactNode
  /** The green bar on a phone, short enough for one line. */
  appliedShort: ReactNode
}

const WORDING: Record<PromoCode, Wording> = {
  WELCOME40: {
    offer: (
      <>
        Click here to save <strong className="font-bold">40% on your first kit</strong>
      </>
    ),
    applied: (
      <>
        <strong className="font-bold">40% off your first kit</strong> will be automatically applied
        at checkout
      </>
    ),
    appliedShort: (
      <>
        Your <strong className="font-bold">40% off</strong> is applied at checkout
      </>
    ),
  },
  FIRSTFREE: {
    offer: (
      <>
        Click here to get <strong className="font-bold">Your First Kit FREE</strong>
      </>
    ),
    applied: (
      <>
        <strong className="font-bold">Your First Kit FREE</strong> will be automatically applied at
        checkout
      </>
    ),
    appliedShort: (
      <>
        Your <strong className="font-bold">First Kit FREE</strong> is applied at checkout
      </>
    ),
  },
}

const BAR =
  'flex w-full items-center justify-center gap-2 border-b-2 px-4 py-2.5 text-center text-[15px] leading-snug font-medium sm:text-sm'

export function PromoBanner({ modal }: PromoBannerProps) {
  const code = usePromo((state) => state.code)
  const active = usePromo((state) => state.active)
  const wording = WORDING[code]

  function open() {
    openPromoModal(modal, code, 'PromoBanner')
  }

  return (
    // Polite, so the bar turning green after the sign-up is announced.
    <div aria-live="polite" data-testid="promo-banner">
      {active ? (
        <p
          className={`${BAR} border-transparent bg-promo-applied text-promo-ink`}
          data-testid="promo-applied"
        >
          <CheckIcon />
          <span className="hidden sm:inline">{wording.applied}</span>
          <span className="sm:hidden">{wording.appliedShort}</span>
        </p>
      ) : (
        <button
          type="button"
          onClick={open}
          className={`${BAR} border-transparent bg-promo-offer text-white outline-none transition hover:brightness-110 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset motion-safe:animate-promo-drift`}
          data-testid="promo-offer"
        >
          <span>{wording.offer}</span>
          <ArrowIcon />
        </button>
      )}
    </div>
  )
}
